import { useState, type ReactNode } from 'react';
import { Plus, Trash2, X, Send, Save, Lock, Unlock, Users, Edit3, BarChart3 } from 'lucide-react';
import { DEFAULT_POLL_SETTINGS, PollQuestion, PollSettings } from '../../services/polls';
import toast from 'react-hot-toast';

export interface PollBuilderValues {
  title: string;
  questions: PollQuestion[];
  settings: PollSettings;
}

interface PollBuilderProps {
  onSubmit: (values: PollBuilderValues, status: 'draft' | 'active') => Promise<void>;
  onCancel: () => void;
  initialTitle?: string;
  initialQuestions?: PollQuestion[];
  initialSettings?: Partial<PollSettings>;
  variant?: 'event' | 'library';
  extraSettings?: ReactNode;
}

const QUESTION_TYPES: { value: PollQuestion['type']; label: string }[] = [
  { value: 'multiple-choice', label: 'Multiple Choice' },
  { value: 'yes-no', label: 'Yes / No' },
  { value: 'rating', label: 'Rating' },
  { value: 'free-text', label: 'Free Text' },
];

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

export default function PollBuilder({ onSubmit, onCancel, initialTitle = '', initialQuestions, initialSettings, variant = 'event', extraSettings }: PollBuilderProps) {
  const startingSettings: PollSettings = { ...DEFAULT_POLL_SETTINGS, ...initialSettings };
  const [title, setTitle] = useState(initialTitle);
  const [questions, setQuestions] = useState<PollQuestion[]>(
    initialQuestions || [{ id: generateId(), type: 'multiple-choice', text: '', required: true, options: ['', ''] }]
  );
  const [mandatory, setMandatory] = useState(startingSettings.mandatory);
  const [showToNewUsers, setShowToNewUsers] = useState(startingSettings.showToNewUsers);
  const [editable, setEditable] = useState(startingSettings.editable);
  const [showResultsToAttendees, setShowResultsToAttendees] = useState(startingSettings.showResultsToAttendees);
  const [submitting, setSubmitting] = useState(false);

  const addQuestion = (type: PollQuestion['type']) => {
    const newQ: PollQuestion = {
      id: generateId(),
      type,
      text: '',
      required: true,
    };
    if (type === 'multiple-choice') newQ.options = ['', ''];
    if (type === 'rating') newQ.ratingMax = 5;
    setQuestions([...questions, newQ]);
  };

  const updateQuestion = (index: number, updates: Partial<PollQuestion>) => {
    setQuestions(prev => prev.map((q, i) => i === index ? { ...q, ...updates } : q));
  };

  const removeQuestion = (index: number) => {
    if (questions.length <= 1) {
      toast.error('Poll must have at least one question');
      return;
    }
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const addOption = (qIndex: number) => {
    setQuestions(prev => prev.map((q, i) =>
      i === qIndex ? { ...q, options: [...(q.options || []), ''] } : q
    ));
  };

  const updateOption = (qIndex: number, optIndex: number, value: string) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIndex) return q;
      const opts = [...(q.options || [])];
      opts[optIndex] = value;
      return { ...q, options: opts };
    }));
  };

  const removeOption = (qIndex: number, optIndex: number) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIndex) return q;
      const opts = (q.options || []).filter((_, oi) => oi !== optIndex);
      return { ...q, options: opts };
    }));
  };

  const validate = (): string | null => {
    if (!title.trim()) return 'Poll title is required';
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.text.trim()) return `Question ${i + 1} text is required`;
      if (q.type === 'multiple-choice') {
        const opts = (q.options || []).filter(o => o.trim());
        if (opts.length < 2) return `Question ${i + 1} needs at least 2 options`;
      }
    }
    return null;
  };

  const handleSubmit = async (status: 'draft' | 'active') => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      // Clean up: filter empty MC options, strip undefined fields (Firestore rejects undefined)
      const cleaned = questions.map(q => {
        const base: PollQuestion = { id: q.id, type: q.type, text: q.text, required: q.required };
        if (q.type === 'multiple-choice') base.options = (q.options || []).filter(o => o.trim());
        if (q.type === 'rating') base.ratingMax = q.ratingMax || 5;
        return base;
      });
      await onSubmit(
        { title: title.trim(), questions: cleaned, settings: { mandatory, showToNewUsers, editable, showResultsToAttendees } },
        status
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create poll');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Poll Title</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g., How's the music tonight?"
          className="input w-full"
          maxLength={100}
        />
      </div>

      {/* Mandatory toggle */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {mandatory ? <Lock className="w-4 h-4 text-amber-500" /> : <Unlock className="w-4 h-4 text-gray-400" />}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {mandatory ? 'Mandatory' : 'Optional'}
            </p>
            <p className="text-xs text-gray-500">
              {mandatory ? 'Attendees must answer to continue' : 'Attendees can dismiss this poll'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMandatory(!mandatory)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            mandatory ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            mandatory ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Show to new users toggle */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Users className={`w-4 h-4 ${showToNewUsers ? 'text-purple-500' : 'text-gray-400'}`} />
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {showToNewUsers ? 'Show to new users' : 'Active only'}
            </p>
            <p className="text-xs text-gray-500">
              {showToNewUsers ? 'New attendees always see this poll, even after it\'s closed' : 'Only visible while poll is active'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowToNewUsers(!showToNewUsers)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            showToNewUsers ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            showToNewUsers ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Editable toggle */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Edit3 className={`w-4 h-4 ${editable ? 'text-teal-500' : 'text-gray-400'}`} />
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {editable ? 'Editable' : 'Locked after submission'}
            </p>
            <p className="text-xs text-gray-500">
              {editable ? 'Attendees can update their answers anytime' : 'Responses are locked after submission'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEditable(!editable)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            editable ? 'bg-teal-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            editable ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Show results toggle */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <BarChart3 className={`w-4 h-4 ${showResultsToAttendees ? 'text-green-500' : 'text-gray-400'}`} />
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {showResultsToAttendees ? 'Results visible' : 'Results hidden'}
            </p>
            <p className="text-xs text-gray-500">
              {showResultsToAttendees ? 'Attendees can see the results after answering' : 'Only you see the results'}
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Show results to attendees"
          onClick={() => setShowResultsToAttendees(!showResultsToAttendees)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            showResultsToAttendees ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            showResultsToAttendees ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.map((q, qIndex) => (
          <div key={q.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Q{qIndex + 1}</span>
                  <select
                    value={q.type}
                    onChange={e => updateQuestion(qIndex, { type: e.target.value as PollQuestion['type'], options: e.target.value === 'multiple-choice' ? ['', ''] : undefined, ratingMax: e.target.value === 'rating' ? 5 : undefined })}
                    className="input text-xs py-1 px-2"
                  >
                    {QUESTION_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1 text-xs text-gray-500">
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={e => updateQuestion(qIndex, { required: e.target.checked })}
                      className="rounded"
                    />
                    Required
                  </label>
                </div>
                <input
                  type="text"
                  value={q.text}
                  onChange={e => updateQuestion(qIndex, { text: e.target.value })}
                  placeholder="Enter your question..."
                  className="input w-full"
                  maxLength={200}
                />
              </div>
              <button
                onClick={() => removeQuestion(qIndex)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Remove question"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Multiple choice options */}
            {q.type === 'multiple-choice' && (
              <div className="space-y-2 pl-4">
                {(q.options || []).map((opt, optIndex) => (
                  <div key={optIndex} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />
                    <input
                      type="text"
                      value={opt}
                      onChange={e => updateOption(qIndex, optIndex, e.target.value)}
                      placeholder={`Option ${optIndex + 1}`}
                      className="input flex-1 text-sm py-1"
                      maxLength={100}
                    />
                    {(q.options || []).length > 2 && (
                      <button onClick={() => removeOption(qIndex, optIndex)} className="p-1 text-gray-400 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                {(q.options || []).length < 6 && (
                  <button
                    onClick={() => addOption(qIndex)}
                    className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add option
                  </button>
                )}
              </div>
            )}

            {/* Rating max */}
            {q.type === 'rating' && (
              <div className="pl-4 flex items-center gap-2">
                <span className="text-xs text-gray-500">Max rating:</span>
                <select
                  value={q.ratingMax || 5}
                  onChange={e => updateQuestion(qIndex, { ratingMax: Number(e.target.value) })}
                  className="input text-xs py-1 px-2 w-16"
                >
                  {[3, 4, 5, 7, 10].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Yes/No preview */}
            {q.type === 'yes-no' && (
              <div className="pl-4 flex items-center gap-2">
                <span className="px-3 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">Yes</span>
                <span className="px-3 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium">No</span>
              </div>
            )}

            {/* Free text preview */}
            {q.type === 'free-text' && (
              <div className="pl-4">
                <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-3 py-2 text-xs text-gray-400">
                  Attendees will type their answer here...
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add question */}
      <div className="flex flex-wrap gap-2">
        {QUESTION_TYPES.map(t => (
          <button
            key={t.value}
            onClick={() => addQuestion(t.value)}
            className="btn btn-secondary btn-sm text-xs flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {extraSettings}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button onClick={onCancel} className="btn btn-secondary btn-sm">
          Cancel
        </button>
        {variant === 'library' ? (
          <button
            onClick={() => handleSubmit('draft')}
            disabled={submitting}
            className="btn btn-primary btn-sm flex items-center gap-1"
          >
            <Save className="w-4 h-4" /> Save to library
          </button>
        ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSubmit('draft')}
            disabled={submitting}
            className="btn btn-secondary btn-sm flex items-center gap-1"
          >
            <Save className="w-4 h-4" /> Save Draft
          </button>
          <button
            onClick={() => handleSubmit('active')}
            disabled={submitting}
            className="btn btn-primary btn-sm flex items-center gap-1"
          >
            <Send className="w-4 h-4" /> Send Poll
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
